import { useState } from 'react'

import '../style.css'
import '../views/FriterieSection.css'
import '../views/AdminMenuSection.css'
import { useMenuApi } from '../hooks/useMenuApi'
import MenuDisplay from '../components/MenuDisplay'

const API_BASE = process.env.REACT_APP_API_URL ?? 'http://localhost:4000'

const CLOUD_WARN = "⚠️ sauvegarde cloud échouée — risque de perte au prochain redéploiement"

function saveStatus(result, successMsg, successMsgCloud, errorMsg = "Erreur lors de la sauvegarde automatique") {
  if (result === true) return successMsg
  if (result === 'cloudinary_error') return successMsgCloud ?? `${successMsg} (${CLOUD_WARN})`
  return errorMsg
}

const AdminMenuSection = () => {
  const [token, setToken] = useState(
    localStorage.getItem('menu_admin_token') || ''
  )
  const [password, setPassword] = useState('')
  const [status, setStatus] = useState('')
  const [activeTab, setActiveTab] = useState(null)

  // UX SAVE / LOGOUT STATES
  const [saveState, setSaveState] = useState('idle')
  const [logoutConfirm, setLogoutConfirm] = useState(false)

  const loggedIn = !!token

  const { menu, setMenu, loading, error, saveMenu } = useMenuApi({
    load: loggedIn,
    withAuth: true,
  })

  // LOGIN
  const handleLogin = async (e) => {
    e.preventDefault()
    setStatus('')
    try {
      const res = await fetch(`${API_BASE}/api/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password }),
      })
      if (!res.ok) throw new Error('Login failed')

      const data = await res.json()
      localStorage.setItem('menu_admin_token', data.token)
      setToken(data.token)
      setPassword('')
    } catch (err) {
      console.error(err)
      setStatus('Mot de passe incorrect')
    }
  }

  // LOGOUT
  const handleLogoutClick = () => {
    if (!logoutConfirm) {
      setLogoutConfirm(true)
      setTimeout(() => setLogoutConfirm(false), 4000)
      return
    }

    localStorage.removeItem('menu_admin_token')
    setToken('')
    setStatus('')
    window.location.href = '/menu'
  }

  // INIT TAB
  if (loggedIn && menu && !activeTab && menu.sections?.[0]) {
    setActiveTab(menu.sections[0].id)
  }

  // HELPERS (inchangés)
  const buildMenuWithBurger = (field, value) => ({
    ...menu,
    burgerDuMois: { ...menu.burgerDuMois, [field]: value },
  })

  const buildMenuWithItem = (sectionIndex, groupIndex, itemIndex, updatedItem) => {
    const sections = [...menu.sections]
    const section = { ...sections[sectionIndex] }
    const groups = [...section.groups]
    const group = { ...groups[groupIndex] }
    const items = [...group.items]
    items[itemIndex] = updatedItem
    group.items = items
    groups[groupIndex] = group
    section.groups = groups
    sections[sectionIndex] = section
    return { ...menu, sections }
  }

  // SAVE (UX VERSION)
  const handleSave = async () => {
    if (!menu) return

    setSaveState('saving')

    try {
      const result = await saveMenu(menu)

      const msg = saveStatus(
        result,
        'Menu sauvegardé sur le site ✓',
        `Menu sauvegardé sur le site ✓ (${CLOUD_WARN})`,
        "Erreur lors de l'enregistrement"
      )

      setStatus(msg)
      setSaveState('success')

      // ⏱️ disparition après 3 secondes
      setTimeout(() => {
        setStatus('')
        setSaveState('idle')
      }, 3000)

    } catch (e) {
      setSaveState('error')
    }
  }
  const handleSaveClick = () => {
    if (saveState !== 'confirm') {
      setSaveState('confirm')
      setTimeout(() => setSaveState('idle'), 4000)
      return
    }

    handleSave()
  }

  // MENU CHANGES (inchangé)
  const handleChangeBurger = (field, value) => {
    const newMenu = buildMenuWithBurger(field, value)
    setMenu(newMenu)

    if (field === 'image') {
      saveMenu(newMenu, { silent: true }).then((result) => {
        if (!result) setStatus("Erreur lors de la sauvegarde automatique de l'image")
        else if (result === 'cloudinary_error')
          setStatus(`⚠️ Image sauvegardée localement mais cloud échoué`)
      })
    }
  }

  const handleSectionField = (sectionIndex, field, value) => {
    setMenu((prev) => {
      const sections = [...prev.sections]
      sections[sectionIndex] = { ...sections[sectionIndex], [field]: value }
      return { ...prev, sections }
    })
  }

  const handleItemField = (sectionIndex, groupIndex, itemIndex, field, value) => {
    if (field === 'image') {
      const currentItem = menu.sections[sectionIndex].groups[groupIndex].items[itemIndex]
      const newMenu = buildMenuWithItem(sectionIndex, groupIndex, itemIndex, {
        ...currentItem,
        image: value,
      })

      setMenu(newMenu)

      saveMenu(newMenu, { silent: true }).then((result) => {
        if (!result) setStatus("Erreur lors de la sauvegarde automatique de l'image")
      })
    } else {
      setMenu((prev) => {
        const sections = [...prev.sections]
        const section = { ...sections[sectionIndex] }
        const groups = [...section.groups]
        const group = { ...groups[groupIndex] }
        const items = [...group.items]
        const item = { ...items[itemIndex], [field]: value }
        items[itemIndex] = item
        group.items = items
        groups[groupIndex] = group
        section.groups = groups
        sections[sectionIndex] = section
        return { ...prev, sections }
      })
    }
  }

  const handleDeleteItem = (sectionIndex, groupIndex, itemIndex) => {
    const sections = [...menu.sections]
    const section = { ...sections[sectionIndex] }
    const groups = [...section.groups]
    const group = { ...groups[groupIndex] }

    const items = group.items.filter((_, i) => i !== itemIndex)
    group.items = items
    groups[groupIndex] = group
    section.groups = groups
    sections[sectionIndex] = section

    const newMenu = { ...menu, sections }
    setMenu(newMenu)

    saveMenu(newMenu, { silent: true }).then((result) => {
      setStatus(saveStatus(result, 'Article supprimé ✓'))
    })
  }

  const handleAddItem = (sectionIndex, groupIndex, newItem) => {
    const sections = [...menu.sections]
    const section = { ...sections[sectionIndex] }
    const groups = [...section.groups]
    const group = { ...groups[groupIndex] }

    const items = [...(group.items || []), newItem]
    group.items = items

    groups[groupIndex] = group
    section.groups = groups
    sections[sectionIndex] = section

    const newMenu = { ...menu, sections }
    setMenu(newMenu)

    saveMenu(newMenu, { silent: true }).then((result) => {
      setStatus(saveStatus(result, 'Article ajouté ✓'))
    })
  }

  const handleChangeSauceList = (newSauces) => {
    setMenu((prev) => ({ ...prev, sauces: newSauces }))
  }

  const handleChangeAnnouncement = (field, value) => {
    setMenu((prev) => ({
      ...prev,
      announcement: { ...(prev.announcement || {}), [field]: value },
    }))
  }

  const handleChangeOpenDays = (field, value) => {
    setMenu((prev) => ({ ...prev, [field]: value }))
  }

  const handleUpdateItem = (sectionIndex, groupIndex, itemIndex, updatedItem) => {
    const newMenu = buildMenuWithItem(sectionIndex, groupIndex, itemIndex, updatedItem)
    setMenu(newMenu)

    saveMenu(newMenu, { silent: true }).then((result) => {
      setStatus(saveStatus(result, 'Modifications sauvegardées ✓'))
    })
  }

  // LOGIN SCREEN
  if (!loggedIn) {
    return (
      <div className="admin-login-overlay">
        <div className="admin-login-modal">

          <div className="admin-login-modal-header">
            <h1 className="admin-login-title">
              Administration du menu
            </h1>

            <button
              type="button"
              className="admin-login-close"
              aria-label="Fermer"
              onClick={() => (window.location.href = '/menu')}
            >
              ×
            </button>
          </div>

          <p className="admin-login-subtitle">
            Entrez le mot de passe
          </p>

          <form onSubmit={handleLogin} className="admin-form">

            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />

            <button
              type="submit"
              className="admin-button admin-button--primary"
            >
              Se connecter
            </button>

            {status && <p className="admin-status">{status}</p>}
          </form>

        </div>
      </div>
    )
  }
  // MAIN UI
  return (
    <>
      



      <MenuDisplay
        menu={menu}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        mode="edit"
        onChangeBurger={handleChangeBurger}
        onChangeSectionField={handleSectionField}
        onChangeItem={handleItemField}
        onUpdateItem={handleUpdateItem}
        onDeleteItem={handleDeleteItem}
        onAddItem={handleAddItem}
        onChangeSauceList={handleChangeSauceList}
        onChangeAnnouncement={handleChangeAnnouncement}
        onChangeOpenDays={handleChangeOpenDays}
        onSaveClick={handleSaveClick}
        onLogout={handleLogoutClick}
        logoutConfirm={logoutConfirm} 
        saveState={saveState}
        

      />
    </>
  )
}

export default AdminMenuSection